import { Button, EButtonColorVariant } from '@outdoorsyco/bonfire';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

export type TFilterPreviewState = 'idle' | 'loading' | 'error';
interface IProps {
  onSubmit?: () => void;
  state: TFilterPreviewState;
  total: number | null;
  isDisabled?: boolean;
  isEscapesSearchPage?: boolean;
  isCampgroundsSearchPage?: boolean;
}

const SearchPreviewResults: React.FC<IProps> = ({
  onSubmit,
  state,
  total,
  isDisabled,
  isEscapesSearchPage,
  isCampgroundsSearchPage,
}) => {
  const intl = useIntl();
  const noResultsMessage = intl.formatMessage({
    defaultMessage: 'No results',
    id: 'Ukeqf0',
    description: 'Search > Filters: label with no results',
  });

  const resultsMessage = isEscapesSearchPage
    ? intl.formatMessage(
        {
          defaultMessage: 'Show {total, plural, one {# Escape} other {# Escapes}}',
          id: 'KHE8uS',
          description: 'Search > Filters: label with preview results - Escapes',
        },
        { total },
      )
    : isCampgroundsSearchPage
      ? intl.formatMessage(
          {
            defaultMessage: 'Show {total, plural, one {# Campground} other {# Campgrounds}}',
            id: 'caV5ZL',
            description: 'Search > Filters: label with preview results - Campgrounds',
          },
          { total },
        )
      : intl.formatMessage(
          {
            defaultMessage: 'Show {total, plural, one {# RV} other {# RVs}}',
            id: 'JZeWmw',
            description: 'Search > Filters: label with preview results',
          },
          { total },
        );

  const defaultMessage = isEscapesSearchPage
    ? intl.formatMessage({
        defaultMessage: 'Show Escapes',
        id: 'HLX/ZS',
        description: 'Search > Filters: default label - Escapes',
      })
    : isCampgroundsSearchPage
      ? intl.formatMessage({
          defaultMessage: 'Show Campgrounds',
          id: 'x1jIhV',
          description: 'Search > Filters: default label - Campgrounds',
        })
      : intl.formatMessage({
          defaultMessage: 'Show RVs',
          id: 'iYo5qy',
          description: 'Search > Filters: default label',
        });

  const handleSubmit = useCallback(() => {
    if (state === 'loading') return;
    onSubmit?.();
  }, [state, onSubmit]);

  const handleClickCapture = useCallback((event: React.MouseEvent) => {
    // Prevent click event from bubbling up to elements underneath the modal
    event.stopPropagation();
  }, []);

  // Determine button label based on state and total
  const getButtonLabel = () => {
    if (state === 'error') {
      return noResultsMessage; // Show "No results" when there's an error
    }
    if (isDisabled) {
      return defaultMessage;
    }
    if (total !== null) {
      return total > 0 ? resultsMessage : noResultsMessage;
    }
    return defaultMessage;
  };

  // Determine if button should be disabled
  const isButtonDisabled =
    (state === 'idle' && total === 0) || // No results in idle state
    state === 'error' || // Disable when there's an error
    isDisabled; // External disabled prop

  return (
    <Button
      className="md:w-auto"
      onClick={handleSubmit}
      onClickCapture={handleClickCapture}
      label={getButtonLabel()}
      variant={EButtonColorVariant.Primary}
      loading={state === 'loading'}
      disabled={isButtonDisabled}
    />
  );
};

export default SearchPreviewResults;
